'use client';
import React, { useEffect, useRef } from 'react';
import { SAAS_SIGNUP_URL } from '@/lib/saas';


export default function HeroSection() {
  const blobRef1 = useRef<HTMLDivElement>(null);
  const blobRef2 = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      if (blobRef1.current) {
        blobRef1.current.style.transform = `translate(${x * 40 - 20}px, ${y * 30 - 15}px)`;
      }
      if (blobRef2.current) {
        blobRef2.current.style.transform = `translate(${-(x * 40 - 20)}px, ${-(y * 30 - 15)}px)`;
      }
    };
    const hero = heroRef.current;
    if (hero) hero.addEventListener('mousemove', handleMouseMove);
    return () => { if (hero) hero.removeEventListener('mousemove', handleMouseMove); };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center pt-20 overflow-hidden noise-overlay"
    >
      {/* Atmospheric background layers */}
      <div className="absolute inset-0 dot-grid-bg opacity-40 z-0" />

      {/* Blob layer 1 */}
      <div
        ref={blobRef1}
        className="absolute top-1/4 -left-32 w-[600px] h-[600px] blob-primary rounded-full z-0 transition-transform duration-700 ease-out"
      />
      {/* Blob layer 2 */}
      <div
        ref={blobRef2}
        className="absolute bottom-0 right-0 w-[500px] h-[500px] blob-secondary rounded-full z-0 transition-transform duration-700 ease-out"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-transparent to-background z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full py-20 md:py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left content */}
          <div className="flex flex-col gap-6 animate-fade-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 w-fit">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-widest text-primary">
                Plataforma All-in-One
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-hero-xl font-black text-foreground leading-tight">
              Gerencie toda a sua{' '}
              <span className="gradient-text">empresa</span>{' '}
              em um único sistema.
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
              Controle vendas, estoque, financeiro, PDV, caixa, cozinha, cardápio digital, relatórios, inteligência artificial e muito mais com uma plataforma completa, moderna e intuitiva.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a
                href={`${SAAS_SIGNUP_URL}?trial=14&utm_source=hero`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-8 py-4 text-base inline-flex items-center justify-center gap-2"
              >
                Testar Gratuitamente por 14 Dias
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                </svg>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-2">
              {[
                '14 dias grátis',
                'Sem cartão de crédito',
                'Cancele quando quiser',
                'Suporte especializado',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Dashboard mockup */}
          <div className="relative flex justify-center lg:justify-end" style={{ animationDelay: '0.3s' }}>
            {/* Outer glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl blur-3xl" />

            {/* Dashboard card */}
            <div className="relative w-full max-w-xl glass-card rounded-2xl overflow-hidden shadow-2xl animate-float">
              {/* Window bar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/50">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/60" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                  <div className="w-3 h-3 rounded-full bg-blue-500/60" />
                </div>
                <div className="text-xs text-muted-foreground font-medium tracking-widest uppercase">NegócioFácil — Dashboard</div>
                <div className="w-12" />
              </div>

              {/* Mock dashboard content */}
              <div className="p-4 bg-card/80">
                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: 'Faturamento', value: 'R$ 48.290', change: '+12%', color: 'text-primary' },
                    { label: 'Lucro', value: 'R$ 18.640', change: '+8%', color: 'text-secondary' },
                    { label: 'Despesas', value: 'R$ 29.650', change: '-3%', color: 'text-red-400' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-muted/60 rounded-xl p-3">
                      <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
                      <p className={`text-sm font-bold ${stat.color}`}>{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.change}</p>
                    </div>
                  ))}
                </div>

                {/* Chart area */}
                <div className="bg-muted/40 rounded-xl p-3 mb-4">
                  <div className="flex items-end gap-1 h-20">
                    {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-sm"
                        style={{
                          height: `${h}%`,
                          background: i === 10 ? 'var(--primary)' : `rgba(30,111,255,${0.15 + i * 0.04})`,
                          transition: `height 0.5s ease ${i * 50}ms`,
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Faturamento — últimos 12 meses</p>
                </div>

                {/* Recent items */}
                <div className="space-y-2">
                  {[
                    { name: 'Venda #1847', time: 'há 2min', val: 'R$ 89,90', status: 'pago' },
                    { name: 'Venda #1846', time: 'há 8min', val: 'R$ 245,00', status: 'pago' },
                    { name: 'Pedido #1845', time: 'há 15min', val: 'R$ 56,50', status: 'cozinha' },
                  ].map((item) => (
                    <div key={item.name} className="flex items-center justify-between bg-muted/30 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${item.status === 'pago' ? 'bg-primary' : 'bg-yellow-400'}`} />
                        <span className="text-xs text-foreground font-medium">{item.name}</span>
                        <span className="text-xs text-muted-foreground">{item.time}</span>
                      </div>
                      <span className="text-xs font-bold text-foreground">{item.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 glass-card rounded-2xl px-4 py-3 shadow-xl hidden sm:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">+127 vendas</p>
                  <p className="text-xs text-muted-foreground">hoje</p>
                </div>
              </div>
            </div>

            {/* Floating AI badge */}
            <div className="absolute -bottom-4 -left-4 glass-card rounded-2xl px-4 py-3 shadow-xl hidden sm:block">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-secondary">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">IA Integrada</p>
                  <p className="text-xs text-muted-foreground">insights em tempo real</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}